import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { FilterModel, IServerSideGetRowsRequest } from 'ag-grid-enterprise';
import type {
  AgGridClientQuery,
  FileStreamResult,
  PagingResponseOfPassengerDataFile,
  PassengerDataFile,
  PassengerDataFileApproveRequest,
} from '@/api/client/models/index.ts';
import type { DatasourceResponse } from '@/models/DatasourceResponse.ts';
import { useApiClient } from '@/hooks/api-hook.ts';
import { dateFormatter } from '@/stores/definitions.js';
import { createFileDownloadResponseHandlerOption, downloadFile } from '@/util/file-download.ts';
import { mapFilterModelToApi, mapSortModelToApi } from '@/util/grid-to-api-mappers.ts';

type GridPassengerDataFile = Omit<PassengerDataFile, 'uploadDate'> & {
  uploadDate?: string | null;
};

function normalizeRows(rows: PassengerDataFile[] | null | undefined): GridPassengerDataFile[] {
  return (rows ?? []).map(entry => {
    const uploadDate = entry.uploadDate ? dateFormatter(entry.uploadDate) : null;
    const formattedUploadDate = uploadDate
      ? `${uploadDate.date} ${uploadDate.timeWithoutSeconds}`
      : null;

    return {
      ...entry,
      uploadDate: formattedUploadDate,
    };
  });
}

export const usePassengerDataFilesStore = defineStore('passengerDataFiles', () => {
  const apiClient = useApiClient();

  const loading = ref<boolean>(false);
  const showUpcomingFlights = ref<boolean>(true);
  const showOnlyRetries = ref<boolean>(false);

  const fieldMap: Record<string, string> = {
    tourOperator: 'touroperator.name',
    uploadDate: 'created',
  };

  function getCollectionRequestBuilder() {
    const rawUrl = `/${import.meta.env.VITE_API_ROOT}/PassengerDataFile?showUpcomingFlights=${showUpcomingFlights.value}&showOnlyRetries=${showOnlyRetries.value}`;
    return apiClient.passengerDataFile.withUrl(rawUrl);
  }

  async function get(request: IServerSideGetRowsRequest): Promise<DatasourceResponse<GridPassengerDataFile>> {
    loading.value = true;

    const { startRow, endRow, sortModel, filterModel } = request;
    const query: AgGridClientQuery = {
      startRow,
      endRow,
      sortModel: mapSortModelToApi(sortModel, fieldMap),
      filterModel: mapFilterModelToApi((filterModel ?? {}) as FilterModel, fieldMap),
    };

    try {
      const response: PagingResponseOfPassengerDataFile | undefined = await getCollectionRequestBuilder().post(query);

      return {
        total: response?.total ?? 0,
        rows: normalizeRows(response?.rows),
      };
    } finally {
      loading.value = false;
    }
  }

  async function getFile(id: number): Promise<FileStreamResult | undefined> {
    const response = await apiClient.passengerDataFile.download.byId(id).get({
      options: [createFileDownloadResponseHandlerOption()],
    });
    return downloadFile(response, `PassengerDataFile-${id}.csv`);
  }

  async function approve(id: number): Promise<ArrayBuffer | undefined> {
    const request: PassengerDataFileApproveRequest = {
      id,
    };

    return await apiClient.passengerDataFile.approve.post(request);
  }

  return {
    loading,
    showUpcomingFlights,
    showOnlyRetries,
    get,
    getFile,
    approve,
  };
});
